import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  startTransition,
} from "react";
import { resolveDirectiveSessionKind } from "../../directives/suggestion/storage/session";
import { floatingBuilderOwnsChoiceAtChipPos } from "../../directives/suggestion/storage/floatingStorage";
import type { Editor } from "@tiptap/core";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import type { DirectiveChipStrategy } from "../../directives/core/chipStrategy";
import type { DirectiveChipEditSegment } from "../../types/directiveChipTypes";
import {
  computeDirectiveChipDerived,
  normalizeRawFromNodeAttrs,
} from "./directiveChipDerived";
import { useDirectiveChipIncompleteAutoOpen } from "./useDirectiveChipIncompleteAutoOpen";
import { useDirectiveChipPopoverFocus } from "./useDirectiveChipPopoverFocus";
import { queueFloatingDirectiveBuilderChoiceFocusAfterChipDefersToFloating } from "../../directives/builder/interaction/focusChoice";
import { useDirectiveChipNodeViewActions } from "./useDirectiveChipNodeViewActions";

type UseDirectiveChipNodeViewArgs = {
  node: { attrs: { raw?: string } };
  editor: Editor;
  getPos: () => number | undefined;
  strategy: DirectiveChipStrategy | null;
};

export const useDirectiveChipNodeView = ({
  node,
  editor,
  getPos,
  strategy,
}: UseDirectiveChipNodeViewArgs) => {
  const [open, setOpen] = useState(false);
  const [editSegment, setEditSegment] =
    useState<DirectiveChipEditSegment | null>(null);
  const [highlightOverride, setHighlightOverride] = useState<number | null>(
    null,
  );
  const [chipSegmentFilterText, setChipSegmentFilterText] = useState("");
  const chipPickerKeyboardRef = useRef<HTMLDivElement>(null);
  const chipSegmentFilterInputRef = useRef<HTMLInputElement>(null);
  const justCompletedRef = useRef(false);
  const autoOpenedForRawRef = useRef<string | null>(null);
  const shouldFocusFirstSlotRef = useRef(false);
  const firstVariableButtonRef = useRef<HTMLDivElement | null>(null);
  const choiceInputRef = useRef<HTMLInputElement>(null);
  const choiceSelectTriggerRef = useRef<HTMLButtonElement>(null);
  const preventCloseOnceRef = useRef(false);
  const focusChoiceInputOnceRef = useRef(false);
  const focusChoiceSelectOnceRef = useRef(false);

  const raw = useMemo(
    () => normalizeRawFromNodeAttrs(node.attrs.raw),
    [node.attrs.raw],
  );

  const storage = editor.storage as {
    variableToken?: { getVariables: () => Variables[] };
  };
  const allVariables = useMemo(
    () => storage.variableToken?.getVariables() ?? [],
    [storage.variableToken],
  );

  const {
    parsed,
    config,
    filteredVariables,
    needsVariable,
    needsNextChoice,
    nextChoiceSlot,
    isIncomplete,
  } = useMemo(
    () => computeDirectiveChipDerived(raw, allVariables, strategy),
    [raw, allVariables, strategy],
  );

  useDirectiveChipIncompleteAutoOpen({
    isIncomplete,
    raw,
    autoOpenedForRawRef,
    shouldFocusFirstSlotRef,
    setOpen,
    editor,
    getPos,
  });

  const floatingOwnsChoiceUi = useMemo(
    () => floatingBuilderOwnsChoiceAtChipPos(editor, getPos),
    [editor, getPos],
  );

  /* eslint-disable @eslint-react/set-state-in-effect -- defer chip close + focus handoff until after paint when floating builder takes choice UI */
  useEffect(() => {
    if (!open || !floatingOwnsChoiceUi) return;
    setOpen(false);
    return queueFloatingDirectiveBuilderChoiceFocusAfterChipDefersToFloating();
  }, [open, floatingOwnsChoiceUi]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  useEffect(() => {
    startTransition(() => {
      setChipSegmentFilterText("");
    });
  }, [editSegment]);

  const { handlePopoverContentOpenAutoFocus } = useDirectiveChipPopoverFocus({
    editor,
    getPos,
    editSegment,
    needsVariable,
    needsNextChoice,
    open,
    choicesLength: parsed?.choices.length ?? 0,
    nextChoiceSlot,
    chipPickerKeyboardRef,
    chipSegmentFilterInputRef,
    choiceInputRef,
    choiceSelectTriggerRef,
    shouldFocusFirstSlotRef,
    focusChoiceInputOnceRef,
    focusChoiceSelectOnceRef,
  });

  const {
    availableDirectives,
    handleSelectVariable,
    handleChoiceSubmit,
    handleSelectDirective,
    handleUpdateVariableOnly,
    handleUpdateChoiceOnly,
    handleReorderPrintTableChoices,
    handleSegmentClick,
  } = useDirectiveChipNodeViewActions({
    editor,
    getPos,
    parsed,
    config,
    allVariables,
    strategy,
    justCompletedRef,
    preventCloseOnceRef,
    focusChoiceSelectOnceRef,
    focusChoiceInputOnceRef,
    setOpen,
    setEditSegment,
  });

  const derivedPickerIndex = useMemo(() => {
    if (!open) return 0;
    if (editSegment === "directive" && parsed != null) {
      const i = availableDirectives.findIndex(
        (d) => d.key === parsed.directive,
      );
      return i >= 0 ? i : 0;
    }
    if (editSegment === "variable" && parsed != null) {
      const i = filteredVariables.findIndex((v) => v.name === parsed.variable);
      return i >= 0 ? i : 0;
    }
    if (editSegment === null && needsVariable) return 0;
    return 0;
  }, [
    open,
    editSegment,
    parsed,
    needsVariable,
    availableDirectives,
    filteredVariables,
  ]);

  useEffect(() => {
    startTransition(() => {
      setHighlightOverride(null);
    });
  }, [derivedPickerIndex, open, editSegment]);

  const chipPickerIndex = highlightOverride ?? derivedPickerIndex;

  const setChipPickerIndex = useCallback(
    (update: number | ((prev: number) => number)) => {
      setHighlightOverride((prev) => {
        const effective = prev ?? derivedPickerIndex;
        return typeof update === "function" ? update(effective) : update;
      });
    },
    [derivedPickerIndex],
  );

  const handleChipSegmentFilterChange = useCallback(
    (v: string) => {
      setChipSegmentFilterText(v);
      setChipPickerIndex(0);
    },
    [setChipPickerIndex],
  );

  const directiveLabel = config?.label ?? parsed?.directive ?? "@";
  const variablePart = parsed?.variable ?? "";
  const choicesPart = useMemo(() => parsed?.choices ?? [], [parsed]);

  const getChoiceSlotLabel = useCallback(
    (i: number) =>
      strategy != null && parsed != null && config != null
        ? strategy.getChoiceSlotLabel(
            i,
            parsed,
            config,
            allVariables,
            choicesPart,
          )
        : (config?.choiceSlots[i]?.label ?? `Choice ${i + 1}`),
    [strategy, parsed, config, allVariables, choicesPart],
  );

  const handlePopoverCloseAutoFocus = useCallback(
    (e: Event) => {
      if (justCompletedRef.current) {
        e.preventDefault();
        justCompletedRef.current = false;
        return;
      }
      const pos = getPos();
      const session = resolveDirectiveSessionKind(editor, true);
      if (session.kind === "floating" && session.floatingChoiceAtPos === pos) {
        e.preventDefault();
        queueFloatingDirectiveBuilderChoiceFocusAfterChipDefersToFloating();
      }
    },
    [editor, getPos],
  );

  return {
    parsed,
    config,
    allVariables,
    open,
    setOpen,
    editSegment,
    setEditSegment,
    chipPickerIndex,
    setChipPickerIndex,
    chipPickerKeyboardRef,
    preventCloseOnceRef,
    firstVariableButtonRef,
    choiceInputRef,
    choiceSelectTriggerRef,
    filteredVariables,
    availableDirectives,
    needsVariable,
    needsNextChoice,
    nextChoiceSlot,
    choicesPart,
    directiveLabel,
    variablePart,
    getChoiceSlotLabel,
    handlePopoverContentOpenAutoFocus,
    handlePopoverCloseAutoFocus,
    chipSegmentFilterText,
    handleChipSegmentFilterChange,
    chipSegmentFilterInputRef,
    handleSelectDirective,
    handleUpdateVariableOnly,
    handleSelectVariable,
    handleChoiceSubmit,
    handleUpdateChoiceOnly,
    handleReorderPrintTableChoices,
    handleSegmentClick,
  };
};
